/**
 * Chip-list editor for arbitrary-length string lists (keywords, exclusions,
 * tags…) — supports bulk entry: pasting or typing a comma- or newline-
 * separated block splits it into multiple chips in one action, not just one
 * entry at a time. Shared by the workflow canvas's keyword nodes and the
 * Agent form's routing keywords, rather than duplicated in each.
 */
import { useState } from 'react';
import { Box, Chip, Stack, TextField } from '@mui/material';

const DEFAULT_MAX_LENGTH = 300;

export interface ChipListInputProps {
  label: string;
  placeholder: string;
  helperText?: string;
  values: string[];
  onChange: (next: string[]) => void;
  /** Caps the list length client-side — the authoritative cap always lives
   *  server-side too; this only avoids a pointless local overflow. */
  maxLength?: number;
}

export function ChipListInput({
  label,
  placeholder,
  helperText,
  values,
  onChange,
  maxLength = DEFAULT_MAX_LENGTH,
}: ChipListInputProps) {
  const [pending, setPending] = useState('');

  const commit = (raw: string) => {
    const parts = raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const seen = new Set(values.map((v) => v.toLowerCase()));
    const next = [...values];
    for (const p of parts) {
      const key = p.toLowerCase();
      if (seen.has(key) || next.length >= maxLength) continue;
      seen.add(key);
      next.push(p);
    }
    onChange(next);
    setPending('');
  };

  return (
    <Box>
      <TextField
        label={label}
        size="small"
        fullWidth
        placeholder={placeholder}
        helperText={helperText}
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit(pending);
          }
        }}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text');
          if (/[,\n]/.test(text)) {
            e.preventDefault();
            commit(text);
          }
        }}
        onBlur={() => pending.trim() && commit(pending)}
      />
      {values.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
          {values.map((v, i) => (
            <Chip
              key={`${v}-${i}`}
              label={v}
              size="small"
              onDelete={() => onChange(values.filter((_, j) => j !== i))}
              sx={{ fontSize: 12 }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
